import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Select from '../../components/ui/Select';
import FilterPanel from '../../components/ui/FilterPanel';
import BulkActionBar from '../../components/ui/BulkActionBar';
import VendorTable from './components/VendorTable';
import VendorImportModal from './components/VendorImportModal';
import VendorDetailPanel from './components/VendorDetailPanel';
import DuplicateResolutionModal from './components/DuplicateResolutionModal';

const VendorsPage = () => {
  const [vendors, setVendors] = useState([]);
  const [filteredVendors, setFilteredVendors] = useState([]);
  const [selectedVendors, setSelectedVendors] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'companyName', direction: 'asc' });
  const [filters, setFilters] = useState({});
  const [isFilterCollapsed, setIsFilterCollapsed] = useState(false);
  
  // Modal states
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [isDetailPanelOpen, setIsDetailPanelOpen] = useState(false);
  const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [detailPanelMode, setDetailPanelMode] = useState('edit');
  const [duplicates, setDuplicates] = useState([]);

  // Vendor data will be fetched from database
  // TODO: Fetch vendors from Supabase using: await supabase.from('vendors').select('*')
  const mockVendors = [
    // Remove mock data and fetch from database
    {
      id: 1,
      companyName: "Acme Corporation Ltd",
      tradingName: "Acme Corp",
      contactName: "John Smith",
      email: "john.smith@acmecorp.co.uk",
      phone: "020 7123 4567",
      accountReference: "ACM001",
      vatNumber: "GB123456789",
      vatVerified: true,
      addressLine1: "123 Business Park",
      addressLine2: "Suite 45",
      city: "London",
      county: "Greater London",
      postcode: "SW1A 1AA",
      country: "United Kingdom",
      paymentTerms: "NET30",
      creditLimit: "10000.00",
      accountType: "vendor",
      isActive: true,
      matchingRulesCount: 3,
      lastUpdated: "2024-10-10T10:30:00Z",
      createdAt: "2024-01-15T09:00:00Z",
      notes: "Key client with multiple locations across the UK."
    },
    {
      id: 2,
      companyName: "Tech Solutions PLC",
      tradingName: "",
      contactName: "Sarah Johnson",
      email: "sarah@techsolutions.co.uk",
      phone: "0161 234 5678",
      accountReference: "TECH002",
      vatNumber: "GB987654321",
      vatVerified: true,
      addressLine1: "456 Innovation Centre",
      addressLine2: "",
      city: "Manchester",
      county: "Greater Manchester",
      postcode: "M1 2AB",
      country: "United Kingdom",
      paymentTerms: "NET14",
      creditLimit: "5000.00",
      accountType: "vendor",
      isActive: true,
      matchingRulesCount: 1,
      lastUpdated: "2024-10-12T14:20:00Z",
      createdAt: "2024-02-20T11:30:00Z",
      notes: "Technology services provider, prompt payment history."
    },
    {
      id: 3,
      companyName: "Green Energy Solutions Limited",
      tradingName: "GES Ltd",
      contactName: "Michael Brown",
      email: "m.brown@greenenergy.co.uk",
      phone: "0131 345 6789",
      accountReference: "GES003",
      vatNumber: "GB456789123",
      vatVerified: false,
      addressLine1: "789 Renewable Way",
      addressLine2: "Floor 3",
      city: "Edinburgh",
      county: "Midlothian",
      postcode: "EH1 3CD",
      country: "United Kingdom",
      paymentTerms: "NET30",
      creditLimit: "15000.00",
      accountType: "both",
      isActive: true,
      matchingRulesCount: 2,
      lastUpdated: "2024-10-08T16:45:00Z",
      createdAt: "2024-03-10T08:15:00Z",
      notes: "Renewable energy consultancy, both vendor and supplier relationship."
    },
    {
      id: 4,
      companyName: "Digital Marketing Hub",
      tradingName: "DMH",
      contactName: "Emma Wilson",
      email: "emma@dmhub.co.uk",
      phone: "029 2012 3456",
      accountReference: "DMH004",
      vatNumber: "",
      vatVerified: false,
      addressLine1: "321 Creative Quarter",
      addressLine2: "",
      city: "Cardiff",
      county: "South Glamorgan",
      postcode: "CF10 4EF",
      country: "United Kingdom",
      paymentTerms: "NET7",
      creditLimit: "2500.00",
      accountType: "vendor",
      isActive: true,
      matchingRulesCount: 0,
      lastUpdated: "2024-10-14T09:15:00Z",
      createdAt: "2024-04-05T13:20:00Z",
      notes: "Small business, below VAT threshold."
    },
    {
      id: 5,
      companyName: "Continental Logistics Europe",
      tradingName: "",
      contactName: "Pierre Dubois",
      email: "p.dubois@contlog.fr",
      phone: "+33 1 42 86 83 00",
      accountReference: "CLE005",
      vatNumber: "FR12345678901",
      vatVerified: true,
      addressLine1: "15 Rue de Commerce",
      addressLine2: "",
      city: "Paris",
      county: "Île-de-France",
      postcode: "75001",
      country: "France",
      paymentTerms: "NET30",
      creditLimit: "25000.00",
      accountType: "supplier",
      isActive: true,
      matchingRulesCount: 4,
      lastUpdated: "2024-10-11T11:30:00Z",
      createdAt: "2024-05-12T10:45:00Z",
      notes: "European logistics partner, handles international shipments."
    }
  ];

  const mockDuplicates = [
    {
      id: 'dup-1',
      confidence: 0.95,
      matchedFields: ['Company Name', 'VAT Number'],
      existing: {
        id: 1,
        companyName: "Acme Corporation Ltd",
        accountReference: "ACM001",
        contactName: "John Smith",
        email: "john.smith@acmecorp.co.uk",
        vatNumber: "GB123456789",
        phone: "020 7123 4567",
        createdAt: "2024-01-15T09:00:00Z"
      },
      new: {
        companyName: "ACME CORPORATION LIMITED",
        accountReference: "ACME001",
        contactName: "J. Smith",
        email: "john@acmecorp.co.uk",
        vatNumber: "GB123456789",
        phone: "020 7123 4567"
      }
    }
  ];

  // Filter schema for FilterPanel
  const filterSchema = [
    {
      key: 'search',
      type: 'search',
      placeholder: 'Search vendors...'
    },
    {
      key: 'accountType',
      type: 'select',
      label: 'Account Type',
      options: [
        { value: '', label: 'All Types' },
        { value: 'vendor', label: 'Vendor' },
        { value: 'supplier', label: 'Supplier' },
        { value: 'both', label: 'Both' }
      ]
    },
    {
      key: 'country',
      type: 'select',
      label: 'Country',
      options: [
        { value: '', label: 'All Countries' },
        { value: 'United Kingdom', label: 'United Kingdom' },
        { value: 'France', label: 'France' },
        { value: 'Germany', label: 'Germany' },
        { value: 'Ireland', label: 'Ireland' }
      ]
    },
    {
      key: 'vatStatus',
      type: 'select',
      label: 'VAT Status',
      options: [
        { value: '', label: 'All' },
        { value: 'registered', label: 'VAT Registered' },
        { value: 'verified', label: 'VAT Verified' },
        { value: 'unregistered', label: 'Not VAT Registered' }
      ]
    },
    {
      key: 'isActive',
      type: 'select',
      label: 'Status',
      options: [
        { value: '', label: 'All Vendors' },
        { value: 'true', label: 'Active Only' },
        { value: 'false', label: 'Inactive Only' }
      ]
    },
    {
      key: 'dateRange',
      type: 'dateRange',
      label: 'Last Updated'
    }
  ];

  // Bulk actions configuration
  const bulkActions = [
    {
      label: 'Export Selected',
      icon: 'Download',
      onClick: (count) => handleBulkExport(count),
      primary: true
    },
    {
      label: 'Deactivate',
      icon: 'UserX',
      onClick: (count) => handleBulkDeactivate(count),
      requiresConfirmation: true,
      confirmTitle: 'Deactivate Vendors',
      confirmMessage: 'Are you sure you want to deactivate the selected vendors? They will no longer appear in dropdowns.'
    },
    {
      label: 'Delete',
      icon: 'Trash2',
      onClick: (count) => handleBulkDelete(count),
      destructive: true,
      requiresConfirmation: true,
      confirmTitle: 'Delete Vendors',
      confirmMessage: 'This action cannot be undone. All associated matching rules will also be removed.'
    }
  ];

  const { currentOrgId } = useAuth();

  // Fetch vendors from database (org scoped)
  const reloadVendors = async () => {
    try {
      if (!currentOrgId) return;
      const { data, error } = await supabase
        .from('vendors')
        .select('*')
        .eq('org_id', currentOrgId)
        .order('display_name', { ascending: true });

      if (error) throw error;

      // Transform database format to component format
      const transformedVendors = (data || []).map(vendor => ({
        id: vendor.id,
        companyName: vendor.display_name,
        tradingName: vendor.legal_name || '',
        contactName: '',
        email: '',
        phone: '',
        accountReference: vendor.account_ref,
        vatNumber: vendor.vat_reg_no || '',
        vatVerified: false,
        addressLine1: '',
        addressLine2: '',
        city: '',
        county: '',
        postcode: '',
        country: '',
        paymentTerms: '',
        creditLimit: '0.00',
        accountType: 'vendor',
        isActive: vendor.active,
        matchingRulesCount: 0,
        lastUpdated: vendor.imported_at || new Date().toISOString(),
        createdAt: vendor.imported_at || new Date().toISOString(),
        notes: ''
      }));

      setVendors(transformedVendors);
      setFilteredVendors(transformedVendors);
    } catch (error) {
      console.error('Error fetching vendors:', error);
      setVendors([]);
      setFilteredVendors([]);
    }
  };

  useEffect(() => { reloadVendors(); }, [currentOrgId]);

  // Apply filters and search
  useEffect(() => {
    let filtered = [...vendors];

    // Apply search
    if (filters?.search) {
      const query = filters?.search?.toLowerCase();
      filtered = filtered?.filter(vendor =>
        vendor?.companyName?.toLowerCase()?.includes(query) ||
        vendor?.tradingName?.toLowerCase()?.includes(query) ||
        vendor?.contactName?.toLowerCase()?.includes(query) ||
        vendor?.email?.toLowerCase()?.includes(query) ||
        vendor?.accountReference?.toLowerCase()?.includes(query) ||
        vendor?.vatNumber?.toLowerCase()?.includes(query)
      );
    }

    // Apply other filters
    if (filters?.accountType) {
      filtered = filtered?.filter(vendor => vendor?.accountType === filters?.accountType);
    }

    if (filters?.country) {
      filtered = filtered?.filter(vendor => vendor?.country === filters?.country);
    }

    if (filters?.vatStatus) {
      if (filters?.vatStatus === 'registered') {
        filtered = filtered?.filter(vendor => vendor?.vatNumber);
      } else if (filters?.vatStatus === 'verified') {
        filtered = filtered?.filter(vendor => vendor?.vatNumber && vendor?.vatVerified);
      } else if (filters?.vatStatus === 'unregistered') {
        filtered = filtered?.filter(vendor => !vendor?.vatNumber);
      }
    }

    if (filters?.isActive !== undefined && filters?.isActive !== '') {
      const isActive = filters?.isActive === 'true';
      filtered = filtered?.filter(vendor => vendor?.isActive === isActive);
    }

    // Apply date range filter
    if (filters?.dateRange?.from || filters?.dateRange?.to) {
      filtered = filtered?.filter(vendor => {
        const vendorDate = new Date(vendor.lastUpdated);
        const fromDate = filters?.dateRange?.from ? new Date(filters.dateRange.from) : null;
        const toDate = filters?.dateRange?.to ? new Date(filters.dateRange.to) : null;

        if (fromDate && vendorDate < fromDate) return false;
        if (toDate && vendorDate > toDate) return false;
        return true;
      });
    }

    // Apply sorting
    filtered?.sort((a, b) => {
      const aValue = a?.[sortConfig?.key] || '';
      const bValue = b?.[sortConfig?.key] || '';
      
      if (sortConfig?.direction === 'asc') {
        return aValue?.toString()?.localeCompare(bValue?.toString());
      } else {
        return bValue?.toString()?.localeCompare(aValue?.toString());
      }
    });

    setFilteredVendors(filtered);
  }, [vendors, filters, sortConfig]);

  // Event handlers
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev?.key === key && prev?.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleSelectionChange = (newSelection) => {
    setSelectedVendors(newSelection);
  };

  const handleSelectAll = () => {
    setSelectedVendors(filteredVendors?.map(vendor => vendor?.id));
  };

  const handleClearSelection = () => {
    setSelectedVendors([]);
  };

  const handleEditVendor = (vendorId) => {
    const vendor = vendors?.find(v => v?.id === vendorId);
    setSelectedVendor(vendor);
    setDetailPanelMode('edit');
    setIsDetailPanelOpen(true);
  };

  const handleViewVendor = (vendorId) => {
    const vendor = vendors?.find(v => v?.id === vendorId);
    setSelectedVendor(vendor);
    setDetailPanelMode('view');
    setIsDetailPanelOpen(true);
  };

  const handleCreateVendor = () => {
    setSelectedVendor(null);
    setDetailPanelMode('create');
    setIsDetailPanelOpen(true);
  };

  const handleSaveVendor = async (vendorData) => {
    try {
      if (detailPanelMode === 'create') {
        // Insert new vendor into database
        const { data, error } = await supabase
          .from('vendors')
          .insert([{
            display_name: vendorData.companyName,
            legal_name: vendorData.tradingName || null,
            account_ref: vendorData.accountReference,
            vat_reg_no: vendorData.vatNumber || null,
            active: vendorData.isActive,
            imported_at: new Date().toISOString(),
            org_id: currentOrgId
          }])
          .select();

        if (error) throw error;

        // Transform and add to local state
        const newVendor = {
          id: data[0].id,
          companyName: data[0].display_name,
          tradingName: data[0].legal_name || '',
          contactName: vendorData.contactName || '',
          email: vendorData.email || '',
          phone: vendorData.phone || '',
          accountReference: data[0].account_ref,
          vatNumber: data[0].vat_reg_no || '',
          vatVerified: vendorData.vatVerified || false,
          addressLine1: vendorData.addressLine1 || '',
          addressLine2: vendorData.addressLine2 || '',
          city: vendorData.city || '',
          county: vendorData.county || '',
          postcode: vendorData.postcode || '',
          country: vendorData.country || '',
          paymentTerms: vendorData.paymentTerms || '',
          creditLimit: vendorData.creditLimit || '0.00',
          accountType: vendorData.accountType || 'vendor',
          isActive: data[0].active,
          matchingRulesCount: 0,
          lastUpdated: data[0].imported_at,
          createdAt: data[0].imported_at,
          notes: vendorData.notes || ''
        };

        setVendors(prev => [...prev, newVendor]);
      } else {
        // Update existing vendor in database
        const { error } = await supabase
          .from('vendors')
          .update({
            display_name: vendorData.companyName,
            legal_name: vendorData.tradingName || null,
            account_ref: vendorData.accountReference,
            vat_reg_no: vendorData.vatNumber || null,
            active: vendorData.isActive,
            imported_at: new Date().toISOString()
          })
          .eq('id', vendorData.id)
          .eq('org_id', currentOrgId);

        if (error) throw error;

        // Update local state
        setVendors(prev => prev?.map(vendor => vendor?.id === vendorData?.id ? {
          ...vendor,
          companyName: vendorData.companyName,
          tradingName: vendorData.tradingName,
          accountReference: vendorData.accountReference,
          vatNumber: vendorData.vatNumber,
          isActive: vendorData.isActive,
          lastUpdated: new Date().toISOString()
        } : vendor));
      }
    } catch (error) {
      console.error('Error saving vendor:', error);
      const code = error?.code || '';
      const message = (error?.message || '').toLowerCase();
      const details = (error?.details || '').toLowerCase();

      // Friendly explanations for common cases
      let friendly = 'Failed to save vendor. Please try again.';

      // Unique constraint / duplicate account ref
      if (
        code === '23505' || // Postgres unique_violation
        code === '409' || // PostgREST conflict
        message.includes('duplicate') ||
        message.includes('unique') ||
        details.includes('duplicate') ||
        details.includes('unique')
      ) {
        friendly = `Account Reference "${vendorData?.accountReference || ''}" already exists. Please use a unique Account Reference.`;
      }

      // RLS / permission denied
      else if (
        code === '42501' ||
        message.includes('row-level security') ||
        message.includes('permission denied')
      ) {
        friendly = 'You do not have permission to modify vendors. Ensure your account is admin or relax the vendors write policy for development.';
      }

      // Validation-ish hints
      else if (
        message.includes('null value') || details.includes('null value')
      ) {
        friendly = 'Required fields are missing. Please fill Display Name and Account Reference.';
      }

      alert(friendly);
      throw error;
    }
  };

  const handleDeleteVendor = async (vendorId) => {
    try {
      // Delete from database
      const { error } = await supabase
        .from('vendors')
        .delete()
        .eq('id', vendorId)
        .eq('org_id', currentOrgId);

      if (error) throw error;

      // Update local state
      setVendors(prev => prev?.filter(vendor => vendor?.id !== vendorId));
      setSelectedVendors(prev => prev?.filter(id => id !== vendorId));
    } catch (error) {
      console.error('Error deleting vendor:', error);
      alert('Failed to delete vendor. Please try again.');
      throw error;
    }
  };

  const handleViewMatchingRules = (vendorId) => {
    // Navigate to matching rules page with vendor filter
    console.log('View matching rules for vendor:', vendorId);
  };

  const handleImport = async (importData) => {
    console.log('Import completed:', importData);
    // After modal finishes DB writes, refresh list
    await reloadVendors();
  };

  const handleResolveDuplicates = (resolutions) => {
    console.log('Duplicate resolutions:', resolutions);
    setIsDuplicateModalOpen(false);
    setDuplicates([]);
  };

  const handleBulkExport = (count) => {
    const selectedData = vendors?.filter(vendor => selectedVendors?.includes(vendor?.id));
    console.log(`Exporting ${count} vendors:`, selectedData);
  };

  const handleBulkDeactivate = (count) => {
    setVendors(prev => prev?.map(vendor => 
      selectedVendors?.includes(vendor?.id) 
        ? { ...vendor, isActive: false, lastUpdated: new Date()?.toISOString() }
        : vendor
    ));
    setSelectedVendors([]);
  };

  const handleBulkDelete = (count) => {
    setVendors(prev => prev?.filter(vendor => !selectedVendors?.includes(vendor?.id)));
    setSelectedVendors([]);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-6 py-8">
          {/* Page Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-foreground">Vendors</h1>
              <p className="text-muted-foreground mt-2">
                Manage vendor master list, import from Sage 50, and resolve duplicates
              </p>
            </div>
            
            <div className="flex items-center space-x-3">
              <Button
                variant="outline"
                onClick={() => setIsImportModalOpen(true)}
                iconName="Upload"
                iconPosition="left"
              >
                Import CSV
              </Button>
              
              <Button
                onClick={handleCreateVendor}
                iconName="Plus"
                iconPosition="left"
              >
                Add Vendor
              </Button>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Icon name="Users" size={20} className="text-primary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Vendors</p>
                  <p className="text-2xl font-bold text-foreground">{vendors?.length}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-success/10 rounded-lg flex items-center justify-center">
                  <Icon name="CheckCircle" size={20} className="text-success" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Active</p>
                  <p className="text-2xl font-bold text-foreground">
                    {vendors?.filter(vendor => vendor?.isActive)?.length}
                  </p>
                </div>
              </div>
            </div>
            
            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
                  <Icon name="Receipt" size={20} className="text-accent" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">VAT Registered</p>
                  <p className="text-2xl font-bold text-foreground">
                    {vendors?.filter(vendor => vendor?.vatNumber)?.length}
                  </p>
                </div>
              </div>
            </div>
            
            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-warning/10 rounded-lg flex items-center justify-center">
                  <Icon name="Link" size={20} className="text-warning" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">With Rules</p>
                  <p className="text-2xl font-bold text-foreground">
                    {vendors?.filter(vendor => vendor?.matchingRulesCount > 0)?.length}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="mb-6">
            <FilterPanel
              filters={filters}
              onFiltersChange={handleFiltersChange}
              schema={filterSchema}
              isCollapsed={isFilterCollapsed}
              onToggleCollapse={() => setIsFilterCollapsed(!isFilterCollapsed)}
            />
          </div>

          {/* Bulk Actions */}
          <BulkActionBar
            selectedCount={selectedVendors?.length}
            totalCount={filteredVendors?.length}
            actions={bulkActions}
            onSelectAll={handleSelectAll}
            onClearSelection={handleClearSelection}
            position="sticky"
          />

          {/* Results Summary */}
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-muted-foreground">
              Showing {filteredVendors?.length} of {vendors?.length} vendors
            </p>
            
            <div className="flex items-center space-x-2">
              <span className="text-sm text-muted-foreground">Sort by:</span>
              <Select
                options={[
                  { value: 'companyName', label: 'Company Name' },
                  { value: 'accountReference', label: 'Account Reference' },
                  { value: 'lastUpdated', label: 'Last Updated' },
                  { value: 'contactName', label: 'Contact Name' }
                ]}
                value={sortConfig?.key}
                onChange={(value) => handleSort(value)}
                className="w-40"
              />
            </div>
          </div>

          {/* Vendor Table */}
          <VendorTable
            vendors={filteredVendors}
            selectedVendors={selectedVendors}
            onSelectionChange={handleSelectionChange}
            onEditVendor={handleEditVendor}
            onDeleteVendor={handleDeleteVendor}
            onViewMatchingRules={handleViewMatchingRules}
            sortConfig={sortConfig}
            onSort={handleSort}
          />
        </div>
      </main>
      {/* Modals */}
      <VendorImportModal
        isOpen={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        onImport={handleImport}
      />
      <VendorDetailPanel
        vendor={selectedVendor}
        isOpen={isDetailPanelOpen}
        onClose={() => setIsDetailPanelOpen(false)}
        onSave={handleSaveVendor}
        onDelete={handleDeleteVendor}
        mode={detailPanelMode}
      />
      <DuplicateResolutionModal
        isOpen={isDuplicateModalOpen}
        onClose={() => setIsDuplicateModalOpen(false)}
        duplicates={duplicates}
        onResolve={handleResolveDuplicates}
      />
    </div>
  );
};

export default VendorsPage;
